import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { ForbiddenError } from '@app/errors/http/400/forbidden.error';
import { NotFoundError } from '@app/errors/http/400/not-found.error';
import { UnauthorizedError } from '@app/errors/http/400/unauthorized.error';
import { InternalServerError } from '@app/errors/http/500/internal-server-error.error';
import { Model } from '@app/models/core/base.model';
import { ModelFactory } from '@app/models/core/model-factory';
import { ModelMap } from '@app/models/core/model-map.collection';
import { AuthService } from '@app/services';

/**
 * Abstract class implementation of an Angular resolver.
 * Used to set up data for views and routes automagically.
 *
 * Base for module specific resolvers.
 * Extend this class (make sure to wrap it in the decorator as well)
 * into new modules in order to have a general purpose resolver.
 * All that is needed afterwards is to set the modelFactory property
 * with a list of the module's models.
 */
@Injectable()
export abstract class BaseResolver {
    /**
     * Factory used to generate models in derived resolvers
     */
    protected abstract modelFactory: ModelFactory;
    protected companyId: number;
    protected employeeId: number;

    constructor(
        protected auth: AuthService,
        protected router: Router
    ) {
        this.setLocalVariables();
    }

    /**
     * Bulk of the work for all resolver done here.
     */
    resolve(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<ModelMap> {
        this.setLocalVariables();
        return new Promise<ModelMap>((resolve, reject) => {
            const modelPromises: Promise<Model>[] = [];
            const paramNames: string[] = [];

            for (const modelName of Object.keys(route.params)) {
                if (!this.modelFactory.canGenerate(modelName)) {
                    continue;
                }

                const id = route.params[modelName];
                modelPromises.push(this.modelFactory.generate(modelName, id, route));
                paramNames.push(modelName);
            }

            Promise.all(modelPromises)
                .then((instances) => {
                    const models = new ModelMap();

                    for (let i = 0; i < instances.length; i++) {
                        models.set(paramNames[i], instances[i]);
                    }
                    resolve(models);
                })
                .catch((error) => {
                    if (error instanceof ForbiddenError) {
                        this.router.navigate(['/forbidden']);
                    }
                    if (error instanceof UnauthorizedError) {
                        this.router.navigate(['/forbidden']);
                    }
                    if (error instanceof NotFoundError) {
                        this.router.navigate(['/not-found']);
                    }
                    if (error instanceof InternalServerError) {
                        this.router.navigate(['/server-error']);
                    }
                    reject(error);
                });
        });
    }

    private setLocalVariables(): void {
        // Set some local variables, these are sometimes null
        // when user is already authenticated, other times set
        this.companyId = this.auth.company.id;
        this.employeeId = this.auth.user.id;
    }
}
